import React from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { getPostBySlug, blogPosts } from './posts';
import BlogHero from '../../components/BlogHero';
import HexagonLogo from '../../components/HexagonLogo';
import '../../components/HexagonLogo.css';
import './Blog.css';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.slug !== post.slug)
    .slice(0, 2);

  // If no same-category posts, get other featured posts
  const displayRelated = relatedPosts.length > 0
    ? relatedPosts
    : blogPosts.filter(p => p.slug !== post.slug).slice(0, 2);

  return (
    <>
      <Helmet>
        <title>{post.title} | Fantomize</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <link rel="canonical" href={`https://fantomize.com/blog/${post.slug}`} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://fantomize.com/blog/${post.slug}`} />
        {post.heroImage && <meta property="og:image" content={post.heroImage} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        {post.heroImage && <meta name="twitter:image" content={post.heroImage} />}

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.heroImage,
            "author": {
              "@type": "Organization",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Fantomize",
              "url": "https://fantomize.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://fantomize.com/logo.svg"
              }
            },
            "datePublished": post.date,
            "dateModified": post.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://fantomize.com/blog/${post.slug}`
            },
            "keywords": post.keywords.join(', ')
          })}
        </script>
      </Helmet>

      <div className="blog-page blog-post-page">
        {/* Hero Bar with Navigation and Image */}
        <BlogHero
          breadcrumbs={[
            { label: 'Home', to: '/' },
            { label: 'Blog', to: '/blog' }
          ]}
          currentPage={post.category}
          navLinks={[
            { label: 'All Articles', to: '/blog' },
            { label: 'Search', to: '/search' }
          ]}
          heroImage={post.heroImage}
          heroAlt={post.heroAlt || post.title}
          showPattern={true}
        />

        <div className="blog-container">

          {/* Article Header */}
          <header className="article-header">
            <div className="article-meta-top">
              <span className="article-category">{post.category}</span>
              <span className="meta-divider">•</span>
              <span className="article-read-time">{post.readTime}</span>
            </div>

            <h1>{post.title}</h1>

            <p className="article-excerpt">{post.excerpt}</p>

            <div className="article-byline">
              <div className="author-avatar-small">
                <HexagonLogo size={22} strokeWidth={3} className="hexagon-logo-white" />
              </div>
              <div className="byline-text">
                <span className="author-name">{post.author}</span>
                <span className="article-date">
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <article className="blog-article">
            <div className="article-content">
              <ReactMarkdown
                components={{
                  // Custom renderers for better styling
                  h2: ({node, ...props}) => <h2 className="content-h2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="content-h3" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="pull-quote" {...props} />,
                  table: ({node, ...props}) => <div className="table-wrapper"><table {...props} /></div>,
                  hr: () => <div className="section-divider"><span></span></div>,
                  strong: ({node, ...props}) => <strong className="emphasis" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* Bottom Back Button */}
            <div className="article-bottom-nav">
              <Link to="/blog" className="article-back-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                <span>Back to All Articles</span>
              </Link>
            </div>
          </article>

          {/* Share Section */}
          <div className="share-section">
            <span className="share-label">Share this article</span>
            <div className="share-buttons">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://fantomize.com/blog/${post.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn twitter"
              >
                Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://fantomize.com/blog/${post.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn linkedin"
              >
                LinkedIn
              </a>
              <button
                className="share-btn copy"
                onClick={() => {
                  navigator.clipboard.writeText(`https://fantomize.com/blog/${post.slug}`);
                  alert('Link copied!');
                }}
              >
                Copy Link
              </button>
            </div>
          </div>

          {/* Related Posts */}
          {displayRelated.length > 0 && (
            <section className="related-posts">
              <h2>Continue Reading</h2>
              <div className="related-grid">
                {displayRelated.map(related => (
                  <article key={related.slug} className="related-card">
                    {related.heroImage && (
                      <Link to={`/blog/${related.slug}`} className="related-image-link">
                        <img
                          src={related.heroImage}
                          alt={related.heroAlt || related.title}
                          className="related-image"
                        />
                      </Link>
                    )}
                    <div className="related-content">
                      <span className="related-category">{related.category}</span>
                      <h3>
                        <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                      </h3>
                      <span className="related-date">
                        {new Date(related.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Footer */}
          <footer className="blog-footer">
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/search">Search</Link>
            </div>
            <p className="copyright">&copy; 2026 Fantomize. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default BlogPost;


